import { useState, useMemo, useEffect } from 'react';
import * as XLSX from 'xlsx';

// Firebase Config & Firestore SDK
import { db } from '../../../firebase/config'; 
import { collection, collectionGroup, getDocs, query, where, doc } from 'firebase/firestore';

// --- IMPORTACIÓN DE NUESTRAS UTILIDADES DE SEGUROS ---
import { 
    parseCurrency, 
    parseDateToComparable, 
    formatDateForInput 
} from '../../../utils/segurosUtils';

// --- NUEVO CUSTOM HOOK DE FILTROS MODULAR ---
import { useFiltrosReportes } from './useFiltrosReportes';

// --- PARSER DE FECHA ROBUSTO ---
const parseFechaLimpia = (fechaInput) => {
    if (!fechaInput) return null;
    if (fechaInput instanceof Date) return isNaN(fechaInput.getTime()) ? null : fechaInput;

    const str = String(fechaInput).trim().split('T')[0];

    if (str.includes('/')) {
        const parts = str.split('/');
        if (parts.length === 3) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const year = parseInt(parts[2], 10);
            const d = new Date(year, month, day);
            return isNaN(d.getTime()) ? null : d;
        }
    }

    if (str.includes('-')) {
        const parts = str.split('-');
        if (parts.length === 3) {
            const year = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const day = parseInt(parts[2], 10);
            const d = new Date(year, month, day);
            return isNaN(d.getTime()) ? null : d;
        }
    }

    const d = new Date(fechaInput);
    return isNaN(d.getTime()) ? null : d;
};

// --- CALCULADOR DE ESTADO DE EMISIÓN CRONOLÓGICO SEGURO ---
const calcularEstadoEmision = (estadoEmisionRaw, fInicioRaw, fVencRaw) => {
    const rawUpper = String(estadoEmisionRaw || '').toUpperCase().trim();
    
    if (['ANULADA', 'CANCELADA', 'RECHAZADA'].includes(rawUpper)) {
        return rawUpper;
    }

    const hoyDate = new Date();
    hoyDate.setHours(0, 0, 0, 0);

    const fInicioDate = parseFechaLimpia(fInicioRaw);
    const fVencDate = parseFechaLimpia(fVencRaw);

    if (fInicioDate) fInicioDate.setHours(0, 0, 0, 0);
    if (fVencDate) fVencDate.setHours(0, 0, 0, 0);

    if (fVencDate) {
        if (hoyDate > fVencDate) {
            return 'VENCIDA';
        }
        if (fInicioDate && hoyDate < fInicioDate) {
            return 'NO INICIADA';
        }
        return 'VIGENTE';
    }

    return rawUpper || 'SIN_ESTADO';
};

// --- PARSER DE RUTAS UNIVERSAL ---
const parsePathDetails = (path) => {
    const parts = path.split('/');
    const clientesIndex = parts.indexOf('clientes');
    const polizasIndex = parts.indexOf('polizas');
    
    let clienteId = null;
    let polizaId = null;
    
    if (clientesIndex !== -1 && clientesIndex + 1 < parts.length) {
        clienteId = parts[clientesIndex + 1];
    }
    if (polizasIndex !== -1 && polizasIndex + 1 < parts.length) {
        polizaId = parts[polizasIndex + 1];
    }
    
    let polizaPath = '';
    if (polizasIndex !== -1 && polizaId) {
        polizaPath = parts.slice(0, polizasIndex + 2).join('/');
    }
    
    return { clienteId, polizaId, polizaPath };
};

export const useReportesAvanzados = () => {
    const [fechaDesde, setFechaDesde] = useState('');
    const [fechaHasta, setFechaHasta] = useState('');

    const [dbState, setDbState] = useState({ 
        polizas: [], 
        cobranza: [], 
        siniestros: [], 
        endosos: [], 
        refacturaciones: [], 
        anulaciones: [], 
        renovaciones: [] 
    });
    const [loadingProgress, setLoadingProgress] = useState({ active: false, step: 0, text: '' });
    const [isDbLoaded, setIsDbLoaded] = useState(false);
    const [queryError, setQueryError] = useState(null);

    const [activeTab, setActiveTab] = useState('TABLA'); 
    const [moduloRaiz, setModuloRaiz] = useState('POLIZAS'); 
    const [busquedaGlobal, setBusquedaGlobal] = useState('');
    const [debouncedBusqueda, setDebouncedBusqueda] = useState('');
    const [agruparPor, setAgruparPor] = useState('NINGUNO'); 
    const [openSeccionColumna, setOpenSeccionColumna] = useState('Cliente y Póliza (General)');

    const [columnasSeleccionadas, setColumnasSeleccionadas] = useState([]);

    // --- INTEGRACIÓN DEL CUSTOM HOOK DE FILTROS ---
    const {
        multiFiltros,
        setMultiFiltros,
        resetFiltros,
        opcionesFiltrosUnicas,
        registrosProcesados
    } = useFiltrosReportes({
        dbState,
        moduloRaiz,
        isDbLoaded,
        debouncedBusqueda
    });

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const modParam = params.get('modulo');
        if (modParam) {
            setModuloRaiz(modParam);
        }
    }, []);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedBusqueda(busquedaGlobal);
        }, 300);
        return () => clearTimeout(handler);
    }, [busquedaGlobal]);

    useEffect(() => {
        const columnasDefault = {
            POLIZAS: ['fechaCarga', 'clienteNombre', 'clienteApellido', 'compania', 'nPoliza', 'riesgo', 'estadoEmision', 'primaEmitida', 'vendedor', 'canal', 'canalNombre'],
            COBRANZA: ['fechaCargaCobranza', 'clienteNombre', 'clienteApellido', 'nPoliza', 'compania', 'cuota', 'endoso', 'nroRecibo', 'importe', 'monedaCobranza', 'estado'],
            SINIESTROS: ['fechaCargaSiniestro', 'clienteNombre', 'clienteApellido', 'nPoliza', 'riesgo', 'numeroSiniestro', 'fechaSiniestro', 'tipoSiniestro', 'estadoGestion', 'quienGestiono'],
            ENDOSOS: ['fechaCargaEndoso', 'clienteNombre', 'clienteApellido', 'nPoliza', 'tramite', 'tipoendoso', 'estadoGestion', 'quienGestiono'],
            REFACTURACIONES: ['fechaCargaRefacturaciones', 'clienteNombre', 'clienteApellido', 'nPoliza', 'numeroFactura', 'numeroEndoso', 'inicioVigenciaRefac', 'finVigenciaRefac', 'estadoRefacturacion', 'monedaRefacturacion', 'primaEmitidaRefacturacion', 'premioRefacturacion', 'pagoEfectuado', 'quienCobroRefacturacion'],
            ANULACIONES: ['fechaAnulacion', 'clienteNombre', 'clienteApellido', 'compania', 'nPoliza', 'riesgo', 'primaAnulacion', 'motivoAnulacion', 'quienGestionoAnulacion', 'vendedor'],
            RENOVACIONES: ['fechaCarga', 'inicioVigencia', 'finVigencia', 'clienteNombre', 'clienteApellido', 'compania', 'nPoliza', 'riesgo', 'subriesgo', 'estadoEmision', 'estadoGestionRenovacion', 'polizaExpirante', 'quienRenovo', 'vendedor']
        };
        setColumnasSeleccionadas(columnasDefault[moduloRaiz] || []);
    }, [moduloRaiz]);

    useEffect(() => {
        if (fechaDesde && fechaHasta && moduloRaiz !== 'OBJETIVOS_SANCOR') {
            consultarReportePorRango();
        }
    }, [moduloRaiz]);

    const camposDisponibles = useMemo(() => {
        return {
            'Cliente y Póliza (General)': [
                { key: 'clienteNombre', label: 'Cliente: Nombre' },
                { key: 'clienteApellido', label: 'Cliente: Apellido' },
                { key: 'clienteDni', label: 'Cliente: DNI' },
                { key: 'clienteCuit', label: 'Cliente: CUIT' },
                { key: 'clienteClasificacion', label: 'Cliente: Clasificación' },
                { key: 'compania', label: 'Compañía' },
                { key: 'nPoliza', label: 'N° Póliza' },
                { key: 'riesgo', label: 'Riesgo' },
                { key: 'subriesgo', label: 'Subriesgo' },
                { key: 'canal', label: 'Canal' },
                { key: 'canalNombre', label: 'Nombre Canal' },
                { key: 'subcanalNombre', label: 'Subcanal / Asociado' },
                { key: 'inicioVigencia', label: 'Inicio Vigencia' },
                { key: 'finVigencia', label: 'Vencimiento Final' },
                { key: 'fechaRefacturacion', label: 'Próxima Refacturación' },
                { key: 'estadoEmision', label: 'Estado Emisión' },
                { key: 'formaPago', label: 'Forma de Pago' },
                { key: 'moneda', label: 'Moneda' },
                { key: 'premio', label: 'Premio' },
                { key: 'primaAdelanto', label: 'Prima Adelanto' },
                { key: 'primaEmitida', label: 'Prima Emitida' },
                { key: 'primaPrimeraCuota', label: 'Prima Primera Cuota' },
                { key: 'motivo', label: 'Tipo de Operación' },
                { key: 'subOperacion', label: 'Sub Operación' },
                { key: 'vendedor', label: 'Vendedor' },
                { key: 'suscriptor', label: 'Suscriptor' },
                { key: 'tipoFacturacion', label: 'Tipo Facturación' },
                { key: 'fechaCarga', label: 'Fecha de Carga' },
                { key: 'tieneDeuda', label: 'Tiene Deuda' },
                { key: 'tieneSiniestro', label: 'Tiene Siniestro' },
                { key: 'pagoEfectuado', label: 'Pago Efectuado' }
            ],
            'Control de Renovación': [
                { key: 'estadoGestionRenovacion', label: 'Estado de Renovación' },
                { key: 'quienRenovo', label: 'Quién Renovó' },
                { key: 'polizaExpirante', label: 'Póliza Expirante' },
                { key: 'companiaExpirante', label: 'Compañia Expirante' },
                { key: 'tipoRenovacion', label: 'Tipo de Renovación' }
            ],
            'Anulación': [
                { key: 'fechaAnulacion', label: 'Fecha Anulación' },
                { key: 'quienGestionoAnulacion', label: 'Responsable Anulación' },
                { key: 'primaAnulacion', label: 'Prima Anulación' },
                { key: 'motivoAnulacion', label: 'Motivo Anulación' },
                { key: 'despachoAnulacion', label: 'Despacho Anulación' },
                { key: 'polizaReemplazo', label: 'Póliza Reemplazo Anulación' }
            ],
            'Siniestros': [
                { key: 'fechaCargaSiniestro', label: 'Fecha de Carga (Siniestro)' },
                { key: 'fechaSiniestro', label: 'Fecha Siniestro' },
                { key: 'numeroSiniestro', label: 'N° Siniestro' },
                { key: 'tipoSiniestro', label: 'Tipo de Siniestro' },
                { key: 'cantidadRuedas', label: 'Cantidad Ruedas' },
                { key: 'estadoGestion', label: 'Estado Gestión Siniestro' },
                { key: 'quienGestiono', label: 'Responsable Siniestro' }
            ],
            'Cobranza': [
                { key: 'fechaCargaCobranza', label: 'Fecha de Carga (Cuota)' },
                { key: 'cuota', label: 'Cuota' },
                { key: 'endoso', label: 'Endoso' },
                { key: 'nroRecibo', label: 'N° Recibo' },
                { key: 'monedaCobranza', label: 'Moneda (Cobranza)' },
                { key: 'importe', label: 'Importe Cuota' },
                { key: 'estado', label: 'Estado Cobro' },
                { key: 'estadoDeuda', label: 'Estado Deuda' },
                { key: 'intentoCobro', label: 'Intento de Cobro' },
                { key: 'formaPago', label: 'Forma de Pago (Cobranza)' },
                { key: 'fechaReclamo', label: 'Fecha de Reclamos' },
                { key: 'quienInformo', label: 'Responsable Cobranza' },
                { key: 'motivoDeuda', label: 'Motivo Deuda' },
                { key: 'fechaCobro', label: 'Fecha Cobro' },
                { key: 'quienCobro', label: 'Quién Cobró' }
            ],
            'Endosos': [
                { key: 'fechaCargaEndoso', label: 'Fecha de Carga (Endoso)' },
                { key: 'tramite', label: 'N° Trámite' },
                { key: 'tipoendoso', label: 'Tipo de Endoso' },
                { key: 'estadoGestion', label: 'Estado de Gestión' },
                { key: 'quienGestiono', label: 'Responsable Endoso' }
            ],
            'Refacturación Cauciones': [
                { key: 'fechaCargaRefacturaciones', label: 'Fecha de Carga (Refac)' },
                { key: 'numeroEndoso', label: 'Número Endoso' },
                { key: 'numeroFactura', label: 'Número Factura' },
                { key: 'inicioVigenciaRefac', label: 'Inicio Vigencia (Refac)' },
                { key: 'finVigenciaRefac', label: 'Fin Vigencia (Refac)' },
                { key: 'estadoRefacturacion', label: 'Estado de Refacturación' },
                { key: 'monedaRefacturacion', label: 'Moneda (Refac)' },
                { key: 'primaEmitidaRefacturacion', label: 'Prima Emitida (Refac)' },
                { key: 'premioRefacturacion', label: 'Premio / Prima (Refac)' },
                { key: 'fechaReclamoRefacturacion', label: 'Fecha de Reclamo (Refac)' },
                { key: 'pagoEfectuado', label: 'Pago Efectuado' },
                { key: 'fechaCobroRefacturacion', label: 'Fecha de Cobro (Refac)' },
                { key: 'quienCobroRefacturacion', label: 'Quién Cobró (Refac)' },
                { key: 'fechaDespacho', label: 'Fecha Despacho' },
                { key: 'formaDespacho', label: 'Forma Despacho' }
            ]
        };
    }, []);

    // --- CONSULTA DINÁMICA A LA BASE DE DATOS FIRESTORE ---
    const consultarReportePorRango = async (e) => {
        if (e) e.preventDefault();
        if (!fechaDesde || !fechaHasta || moduloRaiz === 'OBJETIVOS_SANCOR') return;

        setLoadingProgress({ active: true, step: 1, text: 'Identificando rango de búsqueda...' });
        setIsDbLoaded(false);
        setQueryError(null);

        try {
            const coleccionQueryTarget = (moduloRaiz === 'POLIZAS' || moduloRaiz === 'ANULACIONES' || moduloRaiz === 'RENOVACIONES') 
                ? 'polizas' 
                : moduloRaiz === 'COBRANZA' ? 'cobranza'
                : moduloRaiz === 'SINIESTROS' ? 'siniestros'
                : 'endosos';

            const campoFechaQuery = moduloRaiz === 'ANULACIONES' ? 'fechaAnulacion' 
                                  : moduloRaiz === 'RENOVACIONES' ? 'finVigencia'
                                  : 'fechaCarga';

            setLoadingProgress({ active: true, step: 2, text: `Buscando registros indexados por ${campoFechaQuery}...` });
            
            const targetQuery = query(
                collectionGroup(db, coleccionQueryTarget),
                where(campoFechaQuery, '>=', fechaDesde),
                where(campoFechaQuery, '<=', fechaHasta)
            );
            let targetSnap = await getDocs(targetQuery);

            if (targetSnap.empty) {
                try {
                    const dateDesde = parseDateToComparable(fechaDesde);
                    const dateHasta = parseDateToComparable(fechaHasta);
                    if (dateDesde && dateHasta) {
                        dateDesde.setHours(0, 0, 0, 0); 
                        dateHasta.setHours(23, 59, 59, 999); 
                        const fallbackQuery = query(
                            collectionGroup(db, coleccionQueryTarget),
                            where(campoFechaQuery, '>=', dateDesde),
                            where(campoFechaQuery, '<=', dateHasta)
                        );
                        const fallbackSnap = await getDocs(fallbackQuery);
                        if (!fallbackSnap.empty) {
                            targetSnap = fallbackSnap;
                        }
                    }
                } catch (fallbackErr) {
                    console.warn("Falló el intento de consulta por tipo Timestamp:", fallbackErr);
                }
            }

            if (targetSnap.empty) {
                setDbState(prevState => ({ ...prevState, [moduloRaiz.toLowerCase()]: [] }));
                setIsDbLoaded(true);
                setLoadingProgress({ active: false, step: 0, text: '' });
                return;
            }

            setLoadingProgress({ active: true, step: 3, text: 'Sincronizando Asegurados involucrados...' });
            const uniqueClientIds = [...new Set(targetSnap.docs.map(docSnap => {
                const { clienteId } = parsePathDetails(docSnap.ref.path);
                return clienteId;
            }))].filter(Boolean);

            const clientesMap = {};
            const clientChunks = [];
            for (let i = 0; i < uniqueClientIds.length; i += 30) {
                clientChunks.push(uniqueClientIds.slice(i, i + 30));
            }
            await Promise.all(clientChunks.map(async (chunk) => {
                const q = query(collection(db, 'public/data/clientes'), where('__name__', 'in', chunk));
                const snap = await getDocs(q);
                snap.docs.forEach(d => {
                    clientesMap[d.id] = { id: d.id, ...d.data() };
                });
            }));

            setLoadingProgress({ active: true, step: 4, text: 'Sincronizando pólizas vinculadas...' });
            const polizasMap = {};
            const uniquePolicyIds = [...new Set(targetSnap.docs.map(docSnap => {
                const { polizaId } = parsePathDetails(docSnap.ref.path);
                return polizaId;
            }))].filter(Boolean);

            if (uniquePolicyIds.length > 0 && (coleccionQueryTarget !== 'polizas' || moduloRaiz === 'RENOVACIONES')) {
                const uniquePolicyPaths = [...new Set(targetSnap.docs.map(docSnap => {
                    const { polizaPath } = parsePathDetails(docSnap.ref.path);
                    return polizaPath;
                }))].filter(Boolean);

                const policyDocRefs = uniquePolicyPaths.map(pPath => doc(db, pPath));
                const policyChunks = [];
                for (let i = 0; i < policyDocRefs.length; i += 30) {
                    policyChunks.push(policyDocRefs.slice(i, i + 30));
                }

                await Promise.all(policyChunks.map(async (chunk) => {
                    const q = query(collectionGroup(db, 'polizas'), where('__name__', 'in', chunk));
                    const snap = await getDocs(q);
                    snap.docs.forEach(d => {
                        polizasMap[d.id] = d.data();
                    });
                }));
            }

            // --- BATCHING DE PÓLIZAS NUEVAS EN RENOVACIONES ---
            const nuevasPolizasMap = {};
            if (moduloRaiz === 'RENOVACIONES') {
                setLoadingProgress({ active: true, step: 5, text: 'Vincular renovaciones emitidas por lote...' });
                const polizasVencidasNum = [...new Set(targetSnap.docs.map(d => d.data().nPoliza).filter(Boolean))];

                if (polizasVencidasNum.length > 0) {
                    const numChunks = [];
                    for (let i = 0; i < polizasVencidasNum.length; i += 30) {
                        numChunks.push(polizasVencidasNum.slice(i, i + 30));
                    }

                    await Promise.all(numChunks.map(async (chunk) => {
                        const qNuevas = query(
                            collectionGroup(db, 'polizas'),
                            where('polizaExpirante', 'in', chunk)
                        );
                        const snapNuevas = await getDocs(qNuevas);
                        snapNuevas.docs.forEach(d => {
                            const dataN = d.data();
                            if (dataN.polizaExpirante) {
                                const key = dataN.polizaExpirante;
                                const estEmisionRaw = String(dataN.estadoEmision || '').toUpperCase().trim();
                                const esAnulada = estEmisionRaw === 'ANULADA' || estEmisionRaw === 'CANCELADA';

                                if (!nuevasPolizasMap[key]) {
                                    nuevasPolizasMap[key] = dataN;
                                } else {
                                    const anteriorRaw = String(nuevasPolizasMap[key].estadoEmision || '').toUpperCase().trim();
                                    const anteriorEsAnulada = anteriorRaw === 'ANULADA' || anteriorRaw === 'CANCELADA';
                                    
                                    if (anteriorEsAnulada && !esAnulada) {
                                        nuevasPolizasMap[key] = dataN;
                                    }
                                }
                            }
                        });
                    }));
                }
            }

            setLoadingProgress({ active: true, step: 6, text: 'Construyendo modelo relacional de datos...' });
            
            const processedRows = await Promise.all(targetSnap.docs.map(async docSnap => {
                const { clienteId, polizaId } = parsePathDetails(docSnap.ref.path);

                const cData = clientesMap[clienteId] || {};
                const pData = polizasMap[polizaId] || (coleccionQueryTarget === 'polizas' ? docSnap.data() : {});
                const childData = docSnap.data();

                const fInicio = pData.inicioVigencia || pData.fechaInicio;
                const fVenc = pData.finVigencia || pData.fechaVencimiento;
                const estadoEmisionCalculado = calcularEstadoEmision(pData.estadoEmision, fInicio, fVenc);

                const rawPrimaAnulacion = pData.primaAnulacion !== undefined ? pData.primaAnulacion : (pData.primaEmitida || 0);
                const primaAnulacionSaneada = Math.abs(parseCurrency(rawPrimaAnulacion));

                const polizaExpiranteSaneada = pData.polizaExpirante || pData.PolizaExpirante || pData.poliza_expirante || pData.expirante || pData.polizaExpiranteReferencia || '';

                const nuevaPoliza = moduloRaiz === 'RENOVACIONES' ? nuevasPolizasMap[pData.nPoliza] : null;

                const nPolizaNueva = nuevaPoliza?.nPoliza || '';
                const companiaNueva = nuevaPoliza?.compania || '';
                const motivoNuevo = nuevaPoliza?.motivo || nuevaPoliza?.tipoOperacion || '';
                const subOperacionNueva = nuevaPoliza?.subOperacion || '';
                const fInicioNueva = nuevaPoliza?.inicioVigencia || nuevaPoliza?.fechaInicio;
                const fVencNueva = nuevaPoliza?.finVigencia || nuevaPoliza?.fechaVencimiento;
                const estadoEmisionNueva = nuevaPoliza ? calcularEstadoEmision(nuevaPoliza.estadoEmision, fInicioNueva, fVencNueva) : '';
                const primaEmitidaNueva = nuevaPoliza ? Math.abs(parseCurrency(nuevaPoliza.primaEmitida !== undefined ? nuevaPoliza.primaEmitida : nuevaPoliza.premio)) : 0;
                const tieneDeudaNueva = nuevaPoliza ? !!nuevaPoliza.tieneDeuda : false;
                const vendedorNuevo = nuevaPoliza?.vendedor || '';
                const monedaNueva = nuevaPoliza?.moneda || '$';
                const quienGestionoAnulacionNueva = nuevaPoliza?.quienGestionoAnulacion || nuevaPoliza?.quienGestiono || '';
                const motivoAnulacionNueva = nuevaPoliza?.motivoAnulacion || '';
                const fechaAnulacionNueva = formatDateForInput(nuevaPoliza?.fechaAnulacion);
                const primaAnulacionNueva = (nuevaPoliza && nuevaPoliza.primaAnulacion !== undefined && nuevaPoliza.primaAnulacion !== null && nuevaPoliza.primaAnulacion !== '')? Math.abs(parseCurrency(nuevaPoliza.primaAnulacion)) : 0;

                const quienRenovoExpirante = pData.quienRenovo && String(pData.quienRenovo).trim() !== '' 
                    ? String(pData.quienRenovo).trim() 
                    : 'SIN ASIGNAR';
                
                const gestionadoPorReal = pData.gestionadoPor && String(pData.gestionadoPor).trim() !== '' 
                    ? String(pData.gestionadoPor).trim() 
                    : 'SIN ASIGNAR';

                const quienRenovoNuevoDoc = nuevaPoliza?.quienRenovo && String(nuevaPoliza.quienRenovo).trim() !== '' 
                    ? String(nuevaPoliza.quienRenovo).trim() 
                    : 'SIN ASIGNAR';

                const tipoRenovacionReal = pData.tipoRenovacion || pData.formaRenovacion || '';

                const estadoGestionReal = nuevaPoliza 
                    ? 'RENOVADA' 
                    : (pData.estadoGestionRenovacion && String(pData.estadoGestionRenovacion).trim() !== '-' 
                        ? String(pData.estadoGestionRenovacion).trim().toUpperCase() 
                        : '-');

                const parentProperties = {
                    clienteNombre: cData.nombre || childData.clienteNombre || '',
                    clienteApellido: cData.apellido || childData.clienteApellido || '',
                    clienteDni: cData.dni || childData.clienteDni || '',
                    clienteCuit: cData.cuit || childData.clienteCuit || '',
                    clienteClasificacion: cData.clasificacion || 'CALIFICADO',
                    compania: pData.compania || childData.compania || '',
                    nPoliza: pData.nPoliza || childData.nPoliza || '',
                    riesgo: pData.riesgo || childData.riesgo || '',
                    subriesgo: pData.subriesgo || childData.subriesgo || '',
                    canal: pData.canal || childData.canal || '',
                    canalNombre: pData.canalNombre || pData.subcanal || childData.canalNombre || '',
                    subcanalNombre: pData.subcanalNombre || childData.subcanalNombre || '',
                    inicioVigencia: formatDateForInput(fInicio),
                    finVigencia: formatDateForInput(fVenc),
                    fechaRefacturacion: formatDateForInput(pData.fechaRefacturacion),
                    estadoEmision: estadoEmisionCalculado,
                    formaPago: pData.formaPago || childData.formaPago || '',
                    moneda: childData.moneda || pData.moneda || '$', 
                    premio: pData.premio || 0,
                    primaAdelanto: pData.primaAdelanto || 0,
                    primaEmitida: pData.primaEmitida || 0,
                    primaPrimeraCuota: pData.primaPrimeraCuota || 0,
                    motivo: pData.motivo || pData.tipoOperacion || 'EMISION',
                    subOperacion: pData.subOperacion || '',
                    vendedor: pData.vendedor || childData.vendedor || '',
                    suscriptor: pData.suscriptor || childData.suscriptor || '',
                    tipoFacturacion: pData.tipoFacturacion || pData.tipoFacturador || 'MENSUAL',
                    tieneDeuda: pData.tieneDeuda || false,
                    tieneSiniestro: pData.tieneSiniestro || false,
                    fechaAnulacion: formatDateForInput(pData.fechaAnulacion),
                    quienGestionoAnulacion: pData.quienGestionoAnulacion || pData.quienGestiono || '',
                    primaAnulacion: primaAnulacionSaneada,
                    motivoAnulacion: pData.motivoAnulacion || '',
                    despachoAnulacion: pData.despachoAnulacion || '',
                    polizaReemplazo: pData.polizaReemplazo || '',
                    formaRenovacion: pData.formaRenovacion || 'MANUAL',
                    tipoRenovacion: tipoRenovacionReal,
                    estadoGestionRenovacion: estadoGestionReal,
                    
                    quienRenovo: quienRenovoExpirante,
                    gestionadoPor: gestionadoPorReal,
                    quienRenovoNuevo: quienRenovoNuevoDoc,

                    polizaExpirante: polizaExpiranteSaneada,
                    companiaExpirante: pData.companiaExpirante || pData.compania || '',
                    
                    nPolizaNueva,
                    companiaNueva,
                    motivoNuevo,
                    subOperacionNueva,
                    estadoEmisionNueva,
                    primaEmitidaNueva,
                    tieneDeudaNueva,
                    vendedorNuevo,
                    monedaNueva,

                    quienGestionoAnulacionNueva,
                    motivoAnulacionNueva,
                    fechaAnulacionNueva,
                    primaAnulacionNueva,

                    polizaNuevaObj: nuevaPoliza ? {
                        ...nuevaPoliza,
                        estadoEmisionCalculado: estadoEmisionNueva
                    } : null
                };

                const { moneda: childMoneda, ...cleanChildData } = childData;

                const specificProperties = {};
                if (coleccionQueryTarget === 'cobranza') {
                    specificProperties.endoso = childData.endoso || '';
                    specificProperties.cuota = childData.cuota || '';
                    specificProperties.nroRecibo = childData.nroRecibo || '';
                    specificProperties.importe = childData.importe || 0;
                    specificProperties.estado = childData.estado || '';
                    specificProperties.estadoGestion = childData.estado || childData.estadoGestion || '';
                    specificProperties.estadoDeuda = childData.estadoDeuda || '';
                    specificProperties.intentoCobro = childData.intentoCobro !== undefined ? childData.intentoCobro : '';
                    specificProperties.formaPago = childData.formaPago || pData.formaPago || '';
                    specificProperties.motivoDeuda = childData.motivoDeuda || '';
                    specificProperties.quienInformo = childData.quienInformo || '';
                    specificProperties.quienCobro = childData.quienCobro || '';
                    specificProperties.fechaCobro = formatDateForInput(childData.fechaCobro);
                    specificProperties.fechaReclamo = formatDateForInput(childData.fechaReclamo);
                    specificProperties.moneda = childData.moneda || pData.moneda || '$'; 
                    specificProperties.monedaCobranza = childData.moneda || pData.moneda || '$';
                    specificProperties.fechaCargaCobranza = formatDateForInput(childData.fechaCarga); 
                } else if (coleccionQueryTarget === 'siniestros') {
                    specificProperties.fechaSiniestro = formatDateForInput(childData.fechaSiniestro || childData.fechaOcurrencia);
                    specificProperties.numeroSiniestro = childData.numeroSiniestro || childData.nroSiniestro || '';
                    specificProperties.estadoGestion = childData.estadoGestion || childData.estado || '';
                    specificProperties.quienGestiono = childData.quienGestiono || childData.quienGestionoSiniestro || '';
                    specificProperties.tipoSiniestro = childData.tipoSiniestro || '';
                    specificProperties.shadowRuedas = childData.shadowRuedas || '';
                    specificProperties.fechaCargaSiniestro = formatDateForInput(childData.fechaCarga); 
                } else if (coleccionQueryTarget === 'endosos') {
                    specificProperties.quienGestiono = childData.quienGestiono || childData.quienGestionoEndoso || '';
                    specificProperties.numeroEndoso = childData.numeroEndoso || childData.endoso || '';
                    specificProperties.numeroFactura = childData.numeroFactura || '';
                    specificProperties.estadoRefacturacion = childData.estadoRefacturacion || '';
                    specificProperties.monedaRefacturacion = childData.moneda || childData.monedaRefacturacion || '$'; 
                    specificProperties.primaEmitidaRefacturacion = childData.primaEmitida || childData.primaEmitidaRefacturacion || 0;
                    specificProperties.premioRefacturacion = childData.premio || childData.prima || 0;
                    specificProperties.fechaReclamoRefacturacion = formatDateForInput(childData.fechaReclamo);
                    specificProperties.pagoEfectuado = childData.pagoEfectuado || false;
                    specificProperties.fechaCobroRefacturacion = formatDateForInput(childData.fechaCobro);
                    specificProperties.quienCobroRefacturacion = childData.quienCobro || '';
                    specificProperties.fechaDespacho = formatDateForInput(childData.fechaDespacho);
                    specificProperties.formaDespacho = childData.formaDespacho || '';
                    specificProperties.fechaCargaEndoso = formatDateForInput(childData.fechaCarga); 
                    specificProperties.fechaCargaRefacturaciones = formatDateForInput(childData.fechaCarga); 
                    specificProperties.inicioVigenciaRefac = formatDateForInput(childData.inicioVigencia);
                    specificProperties.finVigenciaRefac = formatDateForInput(childData.finVigencia);
                    specificProperties.tramite = childData.tramite || '';
                    specificProperties.tipoendoso = childData.tipoGestion || 'ENDOSO GENERAL';
                    specificProperties.estadoGestion = childData.estadoGestion || '';
                }

                return {
                    id: docSnap.id,
                    path: docSnap.ref.path,
                    clienteId,
                    polizaId,
                    ...cleanChildData, 
                    ...parentProperties, 
                    ...specificProperties 
                };
            }));

            setLoadingProgress({ active: false, step: 0, text: '' });
            
            let endososRows = [];
            let refacturacionesRows = [];
            if (coleccionQueryTarget === 'endosos') {
                endososRows = processedRows.filter(r => 
                    String(r.tipoEndoso || r.tipoGestion || '').trim().toLowerCase() !== 'refacturacion' &&
                    String(r.tipoEndoso || r.tipoGestion || '').trim().toLowerCase() !== 'refacturación'
                );
                refacturacionesRows = processedRows.filter(r => 
                    String(r.tipoEndoso || r.tipoGestion || '').trim().toLowerCase() === 'refacturacion' ||
                    String(r.tipoEndoso || r.tipoGestion || '').trim().toLowerCase() === 'refacturación'
                );
            }

            setDbState(prevState => ({
                ...prevState,
                polizas: moduloRaiz === 'POLIZAS' ? processedRows : prevState.polizas,
                cobranza: moduloRaiz === 'COBRANZA' ? processedRows : prevState.cobranza,
                siniestros: moduloRaiz === 'SINIESTROS' ? processedRows : prevState.siniestros,
                endosos: moduloRaiz === 'ENDOSOS' ? endososRows : prevState.endosos,
                refacturaciones: moduloRaiz === 'REFACTURACIONES' ? refacturacionesRows : prevState.refacturaciones,
                anulaciones: moduloRaiz === 'ANULACIONES' ? processedRows : prevState.anulaciones,
                renovaciones: moduloRaiz === 'RENOVACIONES' ? processedRows : prevState.renovaciones 
            }));

            setIsDbLoaded(true);

        } catch (error) {
            console.error("Error al estructurar BI:", error);
            setQueryError(error.message || "Error al procesar la base de datos.");
            setIsDbLoaded(true);
            setLoadingProgress({ active: false, step: 0, text: '' });
        }
    };

    // --- AGRUPAMIENTO DINÁMICO ---
    const registrosAgrupados = useMemo(() => {
        if (agruparPor === 'NINGUNO') return null;
        const grupos = {};
        registrosProcesados.forEach(row => {
            const key = row[agruparPor] || 'SIN ESPECIFICAR';
            if (!grupos[key]) grupos[key] = { nombre: key, items: [], totalImporte: 0, totalPrima: 0 };
            grupos[key].items.push(row);
            if (row.importe) grupos[key].totalImporte += Number(row.importe) || 0;
            if (row.primaEmitida) grupos[key].totalPrima += Number(row.primaEmitida) || 0;
        });
        return Object.values(grupos).sort((a, b) => b.items.length - a.items.length);
    }, [registrosProcesados, agruparPor]);

    // --- EXPORTAR EXCEL ---
    const exportarExcel = () => {
        if (registrosProcesados.length === 0) return;
        
        const camposMapeados = [];
        Object.values(camposDisponibles).forEach(seccion => {
            camposMapeados.push(...seccion);
        });

        const datosExcel = registrosProcesados.map(row => {
            const celda = {};
            columnasSeleccionadas.forEach(k => {
                const def = camposMapeados.find(c => c.key === k);
                const label = def ? def.label : k;
                let val = row[k];
                if (k === 'tieneDeuda' || k === 'tieneSiniestro' || k === 'pagoEfectuado') val = val ? 'SÍ' : 'NO';
                if ([
                    'importe', 'premio', 'primaAdelanto', 'primaEmitida', 'primaPrimeraCuota', 
                    'importeReclamado', 'primaAnulacion', 'primaEmitidaRefacturacion', 'premioRefacturacion', 'primaRefacturacion'
                ].includes(k)) {
                    val = parseCurrency(val);
                }
                celda[label] = val !== undefined ? val : '';
            });
            return celda;
        });

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(datosExcel);
        ws['!cols'] = columnasSeleccionadas.map(() => ({ wch: 22 }));
        XLSX.utils.book_append_sheet(wb, ws, moduloRaiz);

        if (agruparPor !== 'NINGUNO' && registrosAgrupados) {
            const resumen = registrosAgrupados.map(g => ({ 
                'Grupo': g.nombre, 
                'Casos': g.items.length, 
                'Prima Acumulada': g.totalPrima, 
                'Monto Acumulado (Deudas)': g.totalImporte 
            }));
            const wsR = XLSX.utils.json_to_sheet(resumen);
            XLSX.utils.book_append_sheet(wb, wsR, "Resumen Agrupado");
        }
        XLSX.writeFile(wb, `Reporte_BI_Vigencias_${moduloRaiz}_${fechaDesde}_a_${fechaHasta}.xlsx`);
    };

    return {
        fechaDesde, setFechaDesde,
        fechaHasta, setFechaHasta,
        loadingProgress,
        isDbLoaded,
        queryError,
        activeTab, setActiveTab,
        moduloRaiz, setModuloRaiz,
        columnasSeleccionadas, setColumnasSeleccionadas,
        busquedaGlobal, setBusquedaGlobal,
        agruparPor, setAgruparPor,
        openSeccionColumna, setOpenSeccionColumna,
        multiFiltros, setMultiFiltros,
        resetFiltros,
        camposDisponibles,
        opcionesFiltrosUnicas,
        registrosProcesados,
        registrosAgrupados,
        consultarReportePorRango,
        exportarExcel
    };
};